import { Arg, Ctx, Directive, Mutation, Resolver } from 'type-graphql'
import { In } from 'typeorm'

import { DataSensor } from './data-sensor.js'
import { DataSensorPatch, NewDataSensor } from './data-sensor-type.js'

@Resolver(DataSensor)
export class DataSensorMutation {
  @Directive('@transaction')
  @Mutation(returns => DataSensor, {
    description:
      'Creates a new data sensor configuration for automated data collection. This mutation sets up the connection between hardware devices and datasets.'
  })
  async createDataSensor(
    @Arg('dataSensor') dataSensor: NewDataSensor,
    @Ctx() context: ResolverContext
  ): Promise<DataSensor> {
    const { domain, user, tx } = context.state

    return await tx.getRepository(DataSensor).save({
      ...dataSensor,
      domain,
      creator: user,
      updater: user
    })
  }

  @Directive('@transaction')
  @Mutation(returns => DataSensor, {
    description: 'Updates a data sensor configuration including connection parameters and data mapping settings.'
  })
  async updateDataSensor(
    @Arg('id') id: string,
    @Arg('patch') patch: DataSensorPatch,
    @Ctx() context: ResolverContext
  ): Promise<DataSensor> {
    const { domain, user, tx } = context.state

    const repository = tx.getRepository(DataSensor)
    const dataSensor = await repository.findOne({
      where: { domain: { id: domain.id }, id }
    })

    return await repository.save({
      ...dataSensor,
      ...patch,
      updater: user
    })
  }

  @Directive('@transaction')
  @Mutation(returns => [DataSensor], {
    description:
      'Updates multiple data sensor configurations in a single operation. This mutation supports batch processing for efficient sensor management.'
  })
  async updateMultipleDataSensor(
    @Arg('patches', type => [DataSensorPatch]) patches: DataSensorPatch[],
    @Ctx() context: ResolverContext
  ): Promise<DataSensor[]> {
    const { domain, user, tx } = context.state

    let results = []
    const _createRecords = patches.filter((patch: any) => patch.cuFlag.toUpperCase() === '+')
    const _updateRecords = patches.filter((patch: any) => patch.cuFlag.toUpperCase() === 'M')
    const dataSensorRepo = tx.getRepository(DataSensor)

    if (_createRecords.length > 0) {
      for (let i = 0; i < _createRecords.length; i++) {
        const newRecord = _createRecords[i]

        const result = await dataSensorRepo.save({
          ...newRecord,
          domain,
          creator: user,
          updater: user
        })

        results.push({ ...result, cuFlag: '+' })
      }
    }

    if (_updateRecords.length > 0) {
      for (let i = 0; i < _updateRecords.length; i++) {
        const newRecord = _updateRecords[i]
        const dataSensor = await dataSensorRepo.findOneBy({ id: newRecord.id })

        const result = await dataSensorRepo.save({
          ...dataSensor,
          ...newRecord,
          updater: user
        })

        results.push({ ...result, cuFlag: 'M' })
      }
    }

    return results
  }

  @Directive('@transaction')
  @Mutation(returns => Boolean, {
    description: 'Deletes a data sensor configuration and removes the connection between hardware devices and datasets.'
  })
  async deleteDataSensor(@Arg('id') id: string, @Ctx() context: ResolverContext): Promise<boolean> {
    const { domain, tx } = context.state

    await tx.getRepository(DataSensor).delete({ domain: { id: domain.id }, id })
    return true
  }

  @Directive('@transaction')
  @Mutation(returns => Boolean, {
    description:
      'Deletes multiple data sensor configurations in a single operation. This mutation supports batch deletion for efficient sensor cleanup.'
  })
  async deleteDataSensors(
    @Arg('ids', type => [String]) ids: string[],
    @Ctx() context: ResolverContext
  ): Promise<boolean> {
    const { domain, tx } = context.state

    await tx.getRepository(DataSensor).delete({
      domain: { id: domain.id },
      id: In(ids)
    })

    return true
  }
}
